/**
 * /admin/masters — every master behind a client: identity, registrations,
 * banking, parties, chart of accounts, items, series and employees.
 * @file src/web/features/admin/pages/masters.tsx
 *
 * The "masters" half of Tally's Masters vs Vouchers split. Two tabs:
 * - Groups — set each account group's nature (asset/liability/income/
 * expense); unclassified groups drop off the Balance Sheet, so they're
 * flagged here.
 * - Ledgers — view & correct GSTIN / HSN / rate centrally (the same fields
 * the GST screen fixes inline, but manageable in one place).
 * Scoped to the entity picked in the sidebar.
 */

import { useSearchParams } from 'react-router-dom';
import { Building2, ListTree, Users2, ClipboardCheck, Package, UserRound, Layers } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { useCompany } from '../lib/company-context';
import { GapsTab } from '../components/masters/GapsTab';
import { RegistrationsTab } from '../components/masters/RegistrationsTab';
import { AuditTab } from '../components/masters/AuditTab';
import { BankingTab, SeriesTab, ItemsTab } from '../components/masters/RecordsTabs';
import { EmployeesTab } from '../components/masters/EmployeesTab';
import { ComplianceTab } from '../components/masters/ComplianceTab';
import { ChartTab } from '../components/masters/ChartTab';
import { PickEntity } from '../components/masters/shared';

type Tab = 'gaps' | 'company' | 'chart' | 'parties' | 'items' | 'employees';

const TABS: Tab[] = ['gaps', 'company', 'chart', 'parties', 'items', 'employees'];


export default function MastersPage() {
  const { entityId } = useCompany();

  // The tab lives in the URL so other screens can deep-link to one — Books →
  // Ledgers points at ?tab=chart — and so a reload keeps you where you were.
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab') as Tab | null;
  const tab: Tab = raw && TABS.includes(raw) ? raw : 'gaps';
  const setTab = (t: Tab) => setParams(t === 'gaps' ? {} : { tab: t }, { replace: true });

  return (
    <>
      <SEO title="Masters" />
      <AdminPageHeader title="Masters" breadcrumbs={[{ label: 'oddits' }, { label: 'Masters' }]} />

      {/* One guard for the whole page. Every tab needs a client, so without one
          there is nothing to tab between — six tabs each rendering the same
          "pick a client" message is worse than one that offers the choice. */}
      {!entityId ? <PickEntity /> : (
        <>
          {/* Gaps sits first deliberately: for a newly imported client almost every
              compliance master is empty, and the worklist is the useful entry point. */}
          <div className="mb-6 flex flex-wrap gap-1 rounded-xl border border-ink-200 bg-paper p-1">
            <TabBtn active={tab === 'gaps'} onClick={() => setTab('gaps')} icon={ClipboardCheck} label="Gaps" />
            <TabBtn active={tab === 'company'} onClick={() => setTab('company')} icon={Building2} label="Entity" />
            <TabBtn active={tab === 'parties'} onClick={() => setTab('parties')} icon={Users2} label="Parties" />
            <TabBtn active={tab === 'chart'} onClick={() => setTab('chart')} icon={ListTree} label="Chart of accounts" />
            <TabBtn active={tab === 'items'} onClick={() => setTab('items')} icon={Package} label="Items" />
            <TabBtn active={tab === 'employees'} onClick={() => setTab('employees')} icon={UserRound} label="Employees" />
          </div>

          {tab === 'gaps' && <GapsTab entityId={entityId} onGoToTab={(t) => setTab(({ compliance: 'parties', groups: 'chart', ledgers: 'chart' }[t] ?? t) as Tab)} />}
          {tab === 'company' && (
            <>
              <RegistrationsTab entityId={entityId} />
              <AuditTab entityId={entityId} />
              <BankingTab entityId={entityId} />
              <SeriesTab entityId={entityId} />
            </>
          )}
          {tab === 'parties' && <ComplianceTab entityId={entityId} />}
          {tab === 'chart' && <ChartTab entityId={entityId} onOpenParties={() => setTab('parties')} />}
          {tab === 'items' && <ItemsTab entityId={entityId} />}
          {tab === 'employees' && <EmployeesTab entityId={entityId} />}
        </>
      )}
    </>
  );
}


function TabBtn({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: typeof Layers; label: string }) {
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition ${active ? 'bg-inkblack text-white' : 'text-ink-600 hover:bg-ink-50'}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}
